import { reactive, computed } from "vue";
import { useToast } from "primevue/usetoast";
import { focusFirstInvalidInput, scrollIntoView, scrollToTop, setFocus, focusFirstEnabledInput, focusErrorMessage } from '@/utils/pageUtils';
import { TOAST_MESSAGE_SEVERITY, TOAST_SAVE_LIFE } from "@/utils/constants";

/**
 * Composable function providing helper methods for page state management.
 * @returns {Object} Page helper methods and state.
 */
export function usePageHelper() {
  const toast = useToast();
  const state = reactive({
    isLoading: false,
    isProcessing: false,
    message: 'Loading...',
    isSaving: false,
    hasError: false,
    errorMessage: '',
  });

  /**
   * Set the loading state with a custom message.
   * @param {string} message - The loading message to display.
   */
  const setLoadingState = (message) => {
    state.isLoading = true;
    state.isProcessing = true;
    state.message = message;
    clearError();
  };

  const setErrorMessage = (message) => {
    state.hasError = true;
    state.errorMessage = message;
  };

  /**
   * Set the saving state with a custom message.
   * @param {string} message - The saving message to display.
   */
  const setSavingState = (message) => {
    state.isSaving = true;
    state.isProcessing = true;
    state.message = message;
    clearError();
  };

  /**
   * Set the error state based on the provided error object.
   * @param {Object} error - The error object.
   */
  const setErrorState = (error) => {
    let message = error.message;

    // Check if the error has a response (i.e., the server responded with a status code)
    if (error.response) {
      const statusCode = error.response.status;
      const errorMessage = error.response.data.message || message;

      if (statusCode === 404 || statusCode === 400) {
        // Handle 404 Not Found
        message = errorMessage; // 'Document not found.';
      } else if (statusCode === 401) {
        // Handle 401 Unauthorized
        message = 'Unauthorized.';
      } else {
        // Handle other status codes
        message = `Error ${statusCode}: ${errorMessage}`;
      }
    } else if (error.request) {
      // The request was made but no response was received
      message = 'No response from server. Please check your network.';
    }  

    triggerError(message);
  };

  /**
   * Hide the loading or saving state.
   */
  const hideWaitingState = () => {
    console.log('Hiding waiting state' + state.isProcessing);
    state.isProcessing = false;
    state.isLoading = false;
    state.isSaving = false;
  };

  /**
   * Trigger an error state with a custom message.
   * @param {string} message - The error message to display.
   */
  const triggerError = (message) => {
    state.hasError = true;
    state.errorMessage = message;
  };

  /**
   * Clear the error state.
   */
  const clearError = () => {
    state.hasError = false;
    state.errorMessage = '';
  };

  const showLoading = computed(() => state.isLoading || state.isSaving);

  /**
   * Execute a save function with error handling and state management.
   * @param {Function} saveFunction - The function to execute for saving.
   * @param {string} [message='Saving...'] - The saving message to display.
   * @returns {Promise<any>} The result of the save function.
   */
  const save = async (saveFunction, message = 'Saving...') => {
    setSavingState(message);
    try {
      const result = await saveFunction();
      return result;
    } catch (error) {
      setErrorState(error);
      return null;
    } finally {
      hideWaitingState();
    }
  };

  /**
   * Execute a fetch function with error handling and state management.
   * @param {Function} fetchFunction - The function to execute for fetching data.
   * @param {string} [message='Loading...'] - The loading message to display.
   * @returns {Promise<void>}
   */
  const fetch = async (fetchFunction, message = 'Loading...') => {
    setLoadingState(message);
    try {
      await fetchFunction();
    } catch (error) {
      setErrorState(error);
    } finally {
      hideWaitingState();
    }
  };

  /**
   * Display a toast notification.
   * @param {string} detail - The detail message of the toast.
   * @param {string} [severity=TOAST_MESSAGE_SEVERITY.Success] - The severity level of the toast. 
   * Possible values: 'Success', 'Info', 'Warn', 'Error'.
   * @param {string} [summary='Success'] - The summary title of the toast.
   */
  const showToast = (detail, severity = TOAST_MESSAGE_SEVERITY.Success, summary = 'Success') => {
    toast.add({ group: 'top-toast', severity: severity, summary: summary, detail: detail, life: TOAST_SAVE_LIFE });
  };

  return {
    state,

    // Computed properties
    showLoading,

    fetch,
    save,
    showToast,
    scrollIntoView,
    setFocus,
    focusFirstInvalidInput,
    focusFirstEnabledInput,
    focusErrorMessage,
    scrollToTop,
    triggerError,
    clearError,
    setErrorMessage,
  };
}